import React, { useState, useEffect, useRef, useImperativeHandle, useCallback, forwardRef } from 'react';
import { Info, Calendar, Trophy, Users, Upload, Zap, Image, Camera, X, MapPin, Navigation, DollarSign, ChevronRight } from 'lucide-react';
import toast from 'react-hot-toast';
import { useTheme } from '../../contexts/ThemeContext';
import { sportTypeAPI } from '../../services/api';
import { apiCall } from '../../services/apiUtil';
import { moveImage } from '../../services/imageUploadService';
import { ALLOWED_IMAGE_TYPES } from '../../components/common/ImageCropModal';
import { useImagePicker } from '../../hooks/useImagePicker.jsx';
import { resolveImageUrl } from '../../profile/hooks/useProfileImage';
import ConfirmModal from '../../components/ConfirmModal';
import MatchSettingsComponent from '../../scoring/MatchSettingsComponent';
import TimePicker from '../../components/common/TimePicker';
import TermsWaiverSection from './TermsWaiverSection';
import GooglePlacesAutocomplete from '../../components/common/GooglePlacesAutocomplete';
import { getConfig } from '../../config';
import { PricingPanel, formatMoney } from '../../payments/components/PaymentPriceModal';

const TournamentForm = forwardRef(({
  initialData = {},
  onSubmit,
  onCancel,
  isEditing = false,
  disabled = false,
  submitLabel = "Create Tournament",
  loading = false,
  onImageUploaded = null,
  onImageSelected = null
}, ref) => {
  const { theme } = useTheme();

  // The panel reports its own list; this is only what the collapsed row shows.
  const [showPricing, setShowPricing] = useState(false);
  const [entryPrices, setEntryPrices] = useState(
    initialData?.price ? [initialData.price] : [],
  );

  const [sportTypes, setSportTypes] = useState([]);
  const [dataLoading, setDataLoading] = useState(true);
  // selectedSportName is derived for MatchSettingsComponent (needs string name, not ID)
  const [selectedSportName, setSelectedSportName] = useState('');

  // Helper to convert ISO date to datetime-local format (yyyy-MM-ddThh:mm)
  const toDateTimeLocal = (isoString) => {
    if (!isoString) return '';
    try {
      const date = new Date(isoString);
      if (isNaN(date.getTime())) return '';
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    } catch {
      return '';
    }
  };

  // Date fields that need conversion
  const dateFields = ['startDate', 'endDate', 'registrationDeadline'];

  // Sanitize initialData: replace null values with empty strings, convert dates
  const sanitizedInitialData = Object.fromEntries(
    Object.entries(initialData).map(([key, value]) => {
      if (value === null) return [key, ''];
      if (dateFields.includes(key) && value) {
        return [key, toDateTimeLocal(value)];
      }
      if (key === 'prizeDetail' && value && typeof value === 'object') {
        return [key, value.description ?? ''];
      }
      return [key, value];
    })
  );

  const [formData, setFormData] = useState({
    tournamentName: '',
    description: '',
    startDate: '',
    endDate: '',
    registrationDeadline: '',
    maxParticipants: '',
    registrationFee: '',
    participantsType: '',
    playersPerTeam: '',
    sportTypeId: '',
    facilityId: '',
    venueName: '',
    venueAddress: '',
    venuePlaceId: '',
    venueLat: null,
    venueLng: null,
    venueCity: '',
    venueState: '',
    venueCountry: '',
    venueZipCode: '',
    city: '',
    state: '',
    country: '',
    zipCode: '',
    streetNumber: '',
    streetName: '',
    landmark: '',
    difficultyLevel: 'all_levels',
    tournamentImage: '',
    prizeDetail: '',
    status: 'draft',
    playNow: false,
    matchConfiguration: {},
    requireTerms: false,
    termsContentPath: '',
    requireWaiver: false,
    waiverContentPath: '',
    ...sanitizedInitialData
  });

  // "Ends on same day" — default true; initialize from data if editing
  const [endsOnSameDay, setEndsOnSameDay] = useState(() => {
    const sd = sanitizedInitialData.startDate || '';
    const ed = sanitizedInitialData.endDate || '';
    if (sd && ed) return sd.split('T')[0] === ed.split('T')[0];
    return true;
  });

  const getDatePart = (dt) => (dt ? dt.split('T')[0] : '');
  const getTimePart = (dt) => (dt && dt.includes('T') ? dt.split('T')[1] : '');
  const combineDT = (date, time) => (date && time ? `${date}T${time}` : date ? `${date}T00:00` : '');

  const handleStartDateChange = (date) => {
    setFormData(prev => {
      const newStart = combineDT(date, getTimePart(prev.startDate));
      const updates = { ...prev, startDate: newStart };
      if (endsOnSameDay) {
        const endTime = getTimePart(prev.endDate);
        updates.endDate = endTime ? combineDT(date, endTime) : '';
      }
      return updates;
    });
  };

  const handleStartTimeChange = (time) => {
    setFormData(prev => ({ ...prev, startDate: combineDT(getDatePart(prev.startDate), time) }));
  };

  const handleEndDateChange = (date) => {
    setFormData(prev => ({ ...prev, endDate: combineDT(date, getTimePart(prev.endDate)) }));
  };

  const handleEndTimeChange = (time) => {
    setFormData(prev => {
      const date = endsOnSameDay ? getDatePart(prev.startDate) : getDatePart(prev.endDate);
      return { ...prev, endDate: time ? combineDT(date, time) : '' };
    });
  };

  const handleRegDateChange = (date) => {
    setFormData(prev => ({ ...prev, registrationDeadline: combineDT(date, getTimePart(prev.registrationDeadline)) }));
  };

  const handleRegTimeChange = (time) => {
    setFormData(prev => ({ ...prev, registrationDeadline: combineDT(getDatePart(prev.registrationDeadline), time) }));
  };

  const handleEndsOnSameDayChange = (checked) => {
    setEndsOnSameDay(checked);
    if (checked) {
      setFormData(prev => {
        const endTime = getTimePart(prev.endDate);
        return {
          ...prev,
          endDate: endTime ? combineDT(getDatePart(prev.startDate), endTime) : '',
        };
      });
    }
  };

  const [uploading, setUploading] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const initialFormDataRef = useRef(JSON.stringify(formData));

  const processImageFile = useCallback(async (file) => {
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      toast.error('Please upload a valid image file (JPEG, PNG, GIF, or WebP).')
      return
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image size must be less than 10MB.')
      return
    }
    try {
      const localDataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onloadend = () => resolve(reader.result)
        reader.onerror = reject
        reader.readAsDataURL(file)
      })
      if (onImageSelected) onImageSelected(localDataUrl)
    } catch (error) {
      console.error('[TournamentForm] Failed to read image file:', error)
      toast.error('Failed to read image file.')
    }
  }, [onImageSelected])

  const { openPicker, pickerInput } = useImagePicker({
    accept: 'image/jpeg,image/png,image/gif,image/webp',
    onFiles: (files) => processImageFile(files[0]),
  })

  // Expose methods to parent components
  useImperativeHandle(ref, () => ({
    triggerImageUpload: openPicker,
    applyApprovedImage: handleApprovedImage,
    isUploading: uploading
  }));

  const isFormDirty = () => {
    return JSON.stringify(formData) !== initialFormDataRef.current;
  };

  const handleCancel = () => {
    if (isFormDirty()) {
      setShowCancelConfirm(true);
    } else {
      onCancel();
    }
  };


  const difficulties = [
    { value: 'beginner', label: 'Beginner' },
    { value: 'intermediate', label: 'Intermediate' },
    { value: 'advanced', label: 'Advanced' },
    { value: 'all_levels', label: 'All Levels' }
  ];

  // Load sport types on component mount
  useEffect(() => {
    const loadData = async () => {
      try {
        const sportTypesResponse = await sportTypeAPI.getAll();
        const sportTypesData = sportTypesResponse?.data || sportTypesResponse || [];
        setSportTypes(sportTypesData);
      } catch (error) {
        console.error('Failed to load sport types:', error);
        setSportTypes([
          { id: 18, sportName: 'Basketball' },
          { id: 21, sportName: 'Cricket' },
          { id: 20, sportName: 'Pickleball' }
        ]);
      } finally {
        setDataLoading(false);
      }
    };

    loadData();
  }, []);

  // Update form data when initialData changes
  useEffect(() => {
    if (initialData && Object.keys(initialData).length > 0) {

      // Sanitize: replace null values with empty strings, convert dates
      const sanitized = Object.fromEntries(
        Object.entries(initialData).map(([key, value]) => {
          if (value === null) {
            return [key, ''];
          }
          if (dateFields.includes(key) && value) {
            return [key, toDateTimeLocal(value)];
          }
          // Parse matchConfiguration if it's a string
          if (key === 'matchConfiguration' && typeof value === 'string') {
            try {
              const parsed = JSON.parse(value);
              return [key, parsed];
            } catch (e) {
              console.error('Failed to parse matchConfiguration:', e);
              return [key, {}];
            }
          }
          if (key === 'prizeDetail' && value && typeof value === 'object') {
            return [key, value.description ?? ''];
          }
          return [key, value];
        })
      );

      const finalMatchConfig = sanitized.matchConfiguration || {};

      setFormData(prev => {
        const newData = {
          ...prev,
          ...sanitized,
          // Ensure matchConfiguration is always an object
          matchConfiguration: finalMatchConfig
        };
        return newData;
      });

    }
  }, [initialData]);

  // Resolve sport name for MatchSettingsComponent when sport type ID changes
  useEffect(() => {
    if (formData.sportTypeId && sportTypes.length > 0) {
      const selectedSport = sportTypes.find(s => s.id === parseInt(formData.sportTypeId));
      setSelectedSportName(selectedSport?.sportName?.toLowerCase() || '');
    } else {
      setSelectedSportName('');
    }
  }, [formData.sportTypeId, sportTypes]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };



  // Called by parent after ImageCropModal approves the image (temp URL already uploaded + moderated)
  const handleApprovedImage = async ({ url: tempUrl }) => {
    const tournamentId = initialData?.id;
    if (!tournamentId) {
      // Create mode — just update form preview; parent stores tempUrl and calls moveImage on submit
      setFormData(prev => ({ ...prev, tournamentImage: tempUrl }));
      if (onImageUploaded) onImageUploaded(tempUrl, resolveImageUrl(tempUrl));
      return;
    }

    // Edit mode — move to canonical path now that we have the ID
    setUploading(true);
    try {
      const { url: canonicalUrl } = await moveImage(tempUrl, `tournament_assets/${tournamentId}/images/${crypto.randomUUID()}.jpeg`);
      await apiCall(`/tournaments/${tournamentId}`, {
        method: 'PATCH',
        body: JSON.stringify({ tournamentImage: canonicalUrl })
      });
      setFormData(prev => ({ ...prev, tournamentImage: canonicalUrl }));
      if (onImageUploaded) onImageUploaded(canonicalUrl, resolveImageUrl(canonicalUrl));
      toast.success('Tournament image updated successfully!');
    } catch (error) {
      console.error('[TournamentForm] Image move/save failed:', error);
      toast.error('Failed to save image. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Convert prizeDetail string to JSON if needed
    const submitData = { ...formData };
    if (typeof submitData.prizeDetail === 'string') {
      if (submitData.prizeDetail.trim()) {
        submitData.prizeDetail = { description: submitData.prizeDetail };
      } else {
        delete submitData.prizeDetail;
      }
    }

    // Convert empty numeric fields to null
    if (submitData.maxParticipants === '' || submitData.maxParticipants === null) {
      submitData.maxParticipants = null;
    } else if (submitData.maxParticipants) {
      submitData.maxParticipants = parseInt(submitData.maxParticipants);
    }

    if (submitData.registrationFee === '' || submitData.registrationFee === null) {
      submitData.registrationFee = null;
    } else if (submitData.registrationFee) {
      submitData.registrationFee = parseFloat(submitData.registrationFee);
    }

    if (submitData.playersPerTeam === '' || submitData.playersPerTeam === null) {
      submitData.playersPerTeam = null;
    } else if (submitData.playersPerTeam) {
      submitData.playersPerTeam = parseInt(submitData.playersPerTeam);
    }

    // When same-day end is selected but no end time chosen, block submission
    // (skip when Play Now is enabled — dates are auto-computed)
    if (!submitData.playNow && endsOnSameDay && !getTimePart(submitData.endDate)) {
      toast.error('Please enter an end time — tournament starts and ends on the same day.');
      return;
    }

    // Convert datetime-local strings (no timezone) to UTC ISO so the server
    // (Lambda, UTC) stores the time the user intended in their local timezone.
    ['startDate', 'endDate', 'registrationDeadline'].forEach(field => {
      if (submitData[field]) {
        submitData[field] = new Date(submitData[field]).toISOString();
      }
    });

    onSubmit(submitData);
  };


  return (
    <>
    <form onSubmit={handleSubmit} className="space-y-3">
      {/* Basic Information */}
      <div className={`${theme.backgroundSecondary} rounded-lg p-3 sm:p-4`}>
        <h3 className={`text-sm sm:text-base font-bold ${theme.text} mb-3 flex items-center`}>
          <Info className="w-4 h-4 mr-2 text-blue-500" />
          Basic Information
        </h3>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className={`block text-sm font-medium ${theme.text} mb-1`}>
              Tournament Name *
            </label>
            <input
              type="text"
              name="tournamentName"
              value={formData.tournamentName}
              onChange={handleInputChange}
              placeholder="e.g., Spring Basketball Championship"
              className={`w-full px-3 py-2 border rounded-lg ${theme.input} focus:ring-2 focus:ring-blue-500 focus:border-transparent ${disabled ? 'bg-gray-100 dark:bg-gray-700 cursor-not-allowed' : ''}`}
              required
              disabled={disabled}
            />
          </div>

          <div>
            <label className={`block text-sm font-medium ${theme.text} mb-1`}>
              Sport *
            </label>
            <select
              name="sportTypeId"
              value={formData.sportTypeId}
              onChange={handleInputChange}
              className={`w-full px-3 py-2 border rounded-lg ${theme.input} focus:ring-2 focus:ring-blue-500 focus:border-transparent ${disabled || isEditing ? 'bg-gray-100 dark:bg-gray-700 cursor-not-allowed' : ''}`}
              required
              disabled={disabled || dataLoading || isEditing}
            >
              <option value="">Select a sport...</option>
              {sportTypes.map(sport => (
                <option key={sport.id} value={sport.id}>{sport.sportName}</option>
              ))}
            </select>
          </div>

          <div className="md:col-span-2">
            <label className={`block text-sm font-medium ${theme.text} mb-1`}>
              Description
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="Describe your tournament..."
              rows="2"
              className={`w-full px-3 py-2 border rounded-lg ${theme.input} focus:ring-2 focus:ring-blue-500 focus:border-transparent ${disabled ? 'bg-gray-100 dark:bg-gray-700 cursor-not-allowed' : ''}`}
              disabled={disabled}
            />
          </div>
        </div>
      </div>

      {/* Play Now Toggle + Tournament Dates */}
      <div className={`${theme.backgroundSecondary} rounded-lg p-3 sm:p-4`}>
        <h3 className={`text-sm sm:text-base font-bold ${theme.text} mb-3 flex items-center`}>
          <Calendar className="w-4 h-4 mr-2 text-green-500" />
          Tournament Dates
        </h3>

        {/* Play Now: show when editable, or when already enabled (to display current value) */}
        {(!disabled || formData.playNow) && (
          <div className="mb-4">
            <label className={`flex items-center gap-x-3 cursor-pointer ${disabled ? 'cursor-not-allowed opacity-50' : ''}`}>
              <input
                type="checkbox"
                name="playNow"
                checked={formData.playNow}
                onChange={handleInputChange}
                className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                disabled={disabled}
              />
              <div className="flex items-center">
                <Zap className="w-4 h-4 mr-1 text-yellow-500" />
                <span className={`font-medium ${theme.text}`}>Play Now</span>
                <span className={`text-sm ${theme.textMuted} ml-2`}>(starts immediately, ends in 4 hours)</span>
              </div>
            </label>
          </div>
        )}

        {!formData.playNow && (
          <div className="space-y-3">
            {/* Start Date + Start Time */}
            <div className="grid md:grid-cols-2 gap-4">
              <div style={{ minWidth: 0 }}>
                <label className={`block text-sm font-medium ${theme.text} mb-1`}>Start Date</label>
                <input
                  type="date"
                  value={getDatePart(formData.startDate)}
                  onChange={(e) => handleStartDateChange(e.target.value)}
                  disabled={disabled}
                  className={`w-full px-3 py-2 border ${theme.border} rounded-lg text-sm ${theme.input} focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                />
              </div>
              <div style={{ minWidth: 0 }}>
                <label className={`block text-sm font-medium ${theme.text} mb-1`}>Start Time</label>
                <TimePicker
                  value={getTimePart(formData.startDate)}
                  onChange={handleStartTimeChange}
                  disabled={disabled}
                  placeholder="Select start time"
                />
              </div>
            </div>

            {/* Ends on same day checkbox */}
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={endsOnSameDay}
                onChange={(e) => handleEndsOnSameDayChange(e.target.checked)}
                disabled={disabled}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className={`text-sm font-medium ${theme.text}`}>Ends on same day</span>
            </label>

            {/* End Date (only when different day) + End Time (always) */}
            {endsOnSameDay ? (
              <div className="sm:w-1/2">
                <label className={`block text-sm font-medium ${theme.text} mb-1`}>End Time</label>
                <TimePicker
                  value={getTimePart(formData.endDate)}
                  onChange={handleEndTimeChange}
                  disabled={disabled}
                  placeholder="Select end time"
                />
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-4">
                <div style={{ minWidth: 0 }}>
                  <label className={`block text-sm font-medium ${theme.text} mb-1`}>End Date</label>
                  <input
                    type="date"
                    value={getDatePart(formData.endDate)}
                    onChange={(e) => handleEndDateChange(e.target.value)}
                    disabled={disabled}
                    className={`w-full px-3 py-2 border ${theme.border} rounded-lg text-sm ${theme.input} focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                  />
                </div>
                <div style={{ minWidth: 0 }}>
                  <label className={`block text-sm font-medium ${theme.text} mb-1`}>End Time</label>
                  <TimePicker
                    value={getTimePart(formData.endDate)}
                    onChange={handleEndTimeChange}
                    disabled={disabled}
                    placeholder="Select end time"
                  />
                </div>
              </div>
            )}

            {/* Registration Deadline */}
            <div className="grid md:grid-cols-2 gap-4">
              <div style={{ minWidth: 0 }}>
                <label className={`block text-sm font-medium ${theme.text} mb-1`}>Registration Deadline Date</label>
                <input
                  type="date"
                  value={getDatePart(formData.registrationDeadline)}
                  onChange={(e) => handleRegDateChange(e.target.value)}
                  disabled={disabled || !getDatePart(formData.startDate) || !getTimePart(formData.startDate)}
                  min={new Date().toLocaleDateString('en-CA')}
                  max={getDatePart(formData.startDate) || undefined}
                  title={!getDatePart(formData.startDate) || !getTimePart(formData.startDate) ? 'Set start date & time first' : undefined}
                  className={`w-full px-3 py-2 border ${theme.border} rounded-lg text-sm ${theme.input} focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    disabled || !getDatePart(formData.startDate) || !getTimePart(formData.startDate) ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                />
              </div>
              <div style={{ minWidth: 0 }}>
                <label className={`block text-sm font-medium ${theme.text} mb-1`}>Registration Deadline Time</label>
                <TimePicker
                  value={getTimePart(formData.registrationDeadline)}
                  onChange={handleRegTimeChange}
                  disabled={disabled || !getDatePart(formData.registrationDeadline)}
                  placeholder={!getDatePart(formData.registrationDeadline) ? 'Set deadline date first' : 'Select time'}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Participant Types — shown only when no sport is selected yet.
          Once a sport is selected, participantsType moves into Match Settings
          (rendered by MatchSettingsComponent) so it stays in one place. */}
      {!formData.sportTypeId && (
        <div className={`${theme.backgroundSecondary} rounded-lg p-3 sm:p-4`}>
          <h3 className={`text-sm sm:text-base font-bold ${theme.text} mb-3 flex items-center`}>
            <Users className="w-4 h-4 mr-2 text-blue-500" />
            Participant Types *
          </h3>

          <div className="space-y-2">
            <p className={`text-sm ${theme.textSecondary} mb-2`}>
              Select who can participate in your tournament:
            </p>

            <div className="grid md:grid-cols-2 gap-4">
              {[
                { value: 'singles', label: 'Singles', description: 'Individual players compete one-on-one' },
                { value: 'teams', label: 'Teams', description: 'Teams of multiple players compete' },
              ].map(option => (
                <label key={option.value} className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                  formData.participantsType === option.value
                    ? 'border-blue-500 bg-blue-500/10'
                    : `${theme.border} ${disabled ? '' : theme.cardHover}`
                } ${disabled ? 'cursor-not-allowed' : ''}`}>
                  <input
                    type="radio"
                    name="participantsType"
                    value={option.value}
                    checked={formData.participantsType === option.value}
                    onChange={handleInputChange}
                    className="hidden"
                    disabled={disabled}
                  />
                  <span className={`mr-3 w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                    formData.participantsType === option.value
                      ? 'border-blue-500 bg-blue-500'
                      : `${theme.border}`
                  }`}>
                    {formData.participantsType === option.value && (
                      <span className="w-2 h-2 rounded-full bg-white" />
                    )}
                  </span>
                  <div>
                    <div className={`font-medium ${theme.text}`}>{option.label}</div>
                    <div className={`text-sm ${theme.textMuted}`}>{option.description}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Tournament Details */}
      <div className={`${theme.backgroundSecondary} rounded-lg p-3 sm:p-4`}>
        <h3 className={`text-sm sm:text-base font-bold ${theme.text} mb-3 flex items-center`}>
          <Trophy className="w-4 h-4 mr-2 text-yellow-500" />
          Tournament Details
        </h3>

        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <label className={`block text-sm font-medium ${theme.text} mb-1`}>
              Maximum Participants <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              name="maxParticipants"
              value={formData.maxParticipants}
              onChange={handleInputChange}
              placeholder="e.g., 16"
              min="2"
              className={`w-full px-3 py-2 border rounded-lg ${theme.input} focus:ring-2 focus:ring-blue-500 focus:border-transparent ${disabled ? 'bg-gray-100 dark:bg-gray-700 cursor-not-allowed' : ''} [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`}
              disabled={disabled}
              required
            />
          </div>

          {/* Players per Team - only for team tournaments */}
          {formData.participantsType === 'teams' && (
            <div>
              <label className={`block text-sm font-medium ${theme.text} mb-1`}>
                Players per Team <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                name="playersPerTeam"
                value={formData.playersPerTeam}
                onChange={handleInputChange}
                placeholder="e.g., 5 for 5v5"
                min="1"
                className={`w-full px-3 py-2 border rounded-lg ${theme.input} focus:ring-2 focus:ring-blue-500 focus:border-transparent ${disabled ? 'bg-gray-100 dark:bg-gray-700 cursor-not-allowed' : ''} [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`}
                disabled={disabled}
                required
              />
              <p className={`text-xs ${theme.textMuted} mt-1`}>
                Teams must have exactly this many players to register
              </p>
            </div>
          )}

          <div>
            <label className={`block text-sm font-medium ${theme.text} mb-1`}>
              Venue/Location
            </label>
            {disabled ? (
              <div>
                {formData.venueAddress ? (
                  <div className={`flex items-start gap-x-2 p-3 border rounded-lg ${theme.backgroundSecondary} ${theme.border}`}>
                    <MapPin className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      {formData.venueName && (
                        <p className={`text-sm font-medium ${theme.text}`}>{formData.venueName}</p>
                      )}
                      {formData.venueAddress && (
                        <p className={`text-sm ${theme.textSecondary} break-words`}>{formData.venueAddress}</p>
                      )}
                      {(formData.venueCity || formData.venueState || formData.venueCountry) && (
                        <p className={`text-sm ${theme.textSecondary}`}>
                          {[formData.venueCity, formData.venueState, formData.venueCountry].filter(Boolean).join(', ')}
                        </p>
                      )}
                      {formData.venueLat && formData.venueLng && (
                        <a
                          href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(formData.venueAddress)}${formData.venuePlaceId ? `&query_place_id=${formData.venuePlaceId}` : ''}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-x-1 text-xs text-blue-500 hover:text-blue-600 mt-1"
                        >
                          <Navigation className="w-3 h-3" />
                          <span>Get Directions</span>
                        </a>
                      )}
                    </div>
                  </div>
                ) : (
                  <p className={`text-sm ${theme.textMuted} italic`}>No venue set</p>
                )}
              </div>
            ) : (
              <div className="space-y-2">
                <input
                  type="text"
                  value={formData.venueName || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, venueName: e.target.value }))}
                  placeholder="Venue name (e.g., City Sports Complex)"
                  className={`w-full px-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${theme.input || ''} placeholder:text-gray-400`}
                />
                <GooglePlacesAutocomplete
                  googleApiKey={getConfig('GOOGLE_PLACES_API_KEY')}
                  placeholder="Search for address..."
                  theme={theme}
                  initialValue={[formData.venueAddress, formData.venueCity, formData.venueState, formData.venueCountry].filter(Boolean).join(', ') || ''}
                  onAddressSelect={(addressData) => {
                    setFormData(prev => ({
                      ...prev,
                      venueName: addressData.placeName || addressData.landmark || prev.venueName || '',
                      venueAddress: addressData.fullAddress,
                      venuePlaceId: addressData.placeId,
                      venueLat: addressData.coordinates?.lat || null,
                      venueLng: addressData.coordinates?.lng || null,
                      city: addressData.city || '',
                      state: addressData.state || '',
                      country: addressData.country || '',
                      zipCode: addressData.zipCode || '',
                      streetNumber: addressData.streetNumber || '',
                      streetName: addressData.streetName || '',
                      landmark: addressData.landmark || '',
                      venueCity: addressData.city || '',
                      venueState: addressData.state || '',
                      venueCountry: addressData.country || '',
                      venueZipCode: addressData.zipCode || '',
                    }));
                  }}
                />
                {formData.venueAddress && (
                  <div className={`flex items-center justify-between px-3 py-2 rounded-lg ${theme.backgroundSecondary} border ${theme.border}`}>
                    <div className="flex items-center gap-x-2 min-w-0">
                      <MapPin className="w-4 h-4 text-green-500 flex-shrink-0" />
                      <div className="min-w-0">
                        {formData.venueName && (
                          <p className={`text-xs font-medium ${theme.text} truncate`}>{formData.venueName}</p>
                        )}
                        {formData.venueAddress && (
                          <p className={`text-xs ${theme.textSecondary} truncate`}>{formData.venueAddress}</p>
                        )}
                        {(formData.venueCity || formData.venueState || formData.venueCountry) && (
                          <p className={`text-xs ${theme.textSecondary} truncate`}>
                            {[formData.venueCity, formData.venueState, formData.venueCountry].filter(Boolean).join(', ')}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-x-2 flex-shrink-0 ml-2">
                      <a
                        href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(formData.venueAddress)}${formData.venuePlaceId ? `&query_place_id=${formData.venuePlaceId}` : ''}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-x-1 text-xs text-blue-500 hover:text-blue-600"
                        title="Open in Google Maps"
                      >
                        <Navigation className="w-3 h-3" />
                        <span>Navigate</span>
                      </a>
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, venueName: '', venueAddress: '', venuePlaceId: '', venueLat: null, venueLng: null, city: '', state: '', country: '', zipCode: '', streetNumber: '', streetName: '', landmark: '', venueCity: '', venueState: '', venueCountry: '', venueZipCode: '' }))}
                        className={`text-xs ${theme.textMuted} hover:text-red-500`}
                        title="Clear venue"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ⚠️ WHAT AN ENTRY COSTS IS A PRICE TAG.
          
              This was a bare "Registration Fee (in $)" writing
              `tournaments.registrationfee` — a column NO payment path reads.
              `PaymentSellablesService.tournament()` prices an entry from the
              TAG and returns nothing without one, so an entry fee typed here
              charged nobody. It appeared to work only because creating a
              tournament turns the number into a tag ONCE (`attachFeeAsPrice`);
              every edit after that changed a number nothing reads.
          
              On edit it is the same panel a session and a court are priced
              with. On create there is no tournament id for a tag to hang off,
              so the number is still taken and bridged into a tag on save. */}
          <div>
            <label className={`block text-sm font-medium ${theme.text} mb-1`}>
              <DollarSign size={16} className="inline mr-1" />
              Entry fee
            </label>

            {/* ⚠️ AND ONLY WHERE THERE IS AN ACADEMY TO HOLD THE TAG.
                `createPrice` stores a tag against the SELLER, and `PricingPanel`
                loads nothing without an academy id — so a user-hosted
                tournament would get an empty panel that silently does nothing.
                It keeps the number, which `attachFeeAsPrice` turns into a tag
                owned by the host. */}
            {!isEditing || !initialData?.id
              || initialData?.ownerType !== 'academy' || !initialData?.ownerId ? (
              <>
                <input
                  type="number"
                  name="registrationFee"
                  value={formData.registrationFee}
                  onChange={handleInputChange}
                  placeholder="25"
                  min="0"
                  step="0.01"
                  className={`w-full px-3 py-2 border rounded-lg ${theme.input} focus:ring-2 focus:ring-blue-500 focus:border-transparent ${disabled ? 'bg-gray-100 dark:bg-gray-700 cursor-not-allowed' : ''}`}
                  disabled={disabled}
                />
                <p className={`text-xs ${theme.textMuted} mt-1`}>
                  Becomes this tournament&apos;s price tag. Leave blank for a free entry.
                </p>
              </>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => setShowPricing(v => !v)}
                  className="w-full py-2 rounded-lg text-sm font-medium border border-blue-500 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors flex items-center justify-center gap-1.5"
                >
                  <DollarSign className="w-4 h-4" />
                  {/* An entry with no tag is not free, it is unsellable — the
                      resolver refuses to price it — so the label asks for one
                      rather than showing nothing. */}
                  {entryPrices.length > 0
                    ? `Pricing — ${entryPrices.map(pr => formatMoney(pr.amount, pr.currency)).join(' · ')}`
                    : 'Attach Price'}
                  <ChevronRight className={`w-4 h-4 transition-transform ${showPricing ? 'rotate-90' : ''}`} />
                </button>

                {showPricing && (
                  <div className={`mt-3 pt-3 border-t ${theme.border}`}>
                    <p className={`text-xs ${theme.textMuted} mb-2`}>
                      What one entry costs.
                    </p>
                    <PricingPanel
                      academyId={initialData.ownerId}
                      ownerType="tournament"
                      ownerId={initialData.id}
                      entityName={initialData?.tournamentName || ''}
                      onPricesChange={(list) => setEntryPrices(list.filter(pr => pr.isActive))}
                    />
                  </div>
                )}
              </>
            )}
          </div>

          <div>
            <label className={`block text-sm font-medium ${theme.text} mb-1`}>
              Difficulty Level
            </label>
            <select
              name="difficultyLevel"
              value={formData.difficultyLevel}
              onChange={handleInputChange}
              className={`w-full px-3 py-2 border rounded-lg ${theme.input} focus:ring-2 focus:ring-blue-500 focus:border-transparent ${disabled ? 'bg-gray-100 dark:bg-gray-700 cursor-not-allowed' : ''}`}
              disabled={disabled}
            >
              {difficulties.map(level => (
                <option key={level.value} value={level.value}>{level.label}</option>
              ))}
            </select>
          </div>

          <div className="md:col-span-2">
            <label className={`block text-sm font-medium ${theme.text} mb-1`}>
              Prize Details
            </label>
            <textarea
              name="prizeDetail"
              value={formData.prizeDetail}
              onChange={handleInputChange}
              placeholder="e.g., 1st place: $500, 2nd place: $200, 3rd place: $100"
              rows="2"
              className={`w-full px-3 py-2 border rounded-lg ${theme.input} focus:ring-2 focus:ring-blue-500 focus:border-transparent ${disabled ? 'bg-gray-100 dark:bg-gray-700 cursor-not-allowed' : ''}`}
              disabled={disabled}
            />
          </div>
        </div>
      </div>

      {/* Terms & Conditions / Liability Waiver */}
      <TermsWaiverSection
        formData={formData}
        setFormData={setFormData}
        tournamentId={initialData?.id}
        isEditing={isEditing}
        disabled={disabled}
      />

      {/* Match Settings — rendered by the shared MatchSettingsComponent in compact mode.
          key=sportTypeId remounts the component when the sport changes so state resets. */}
      {selectedSportName && (
        <div className={`${theme.backgroundSecondary} rounded-lg p-3 sm:p-4`}>
          <h3 className={`text-sm sm:text-base font-bold ${theme.text} mb-3 flex items-center`}>
            <Trophy className="w-4 h-4 mr-2 text-orange-500" />
            Match Settings
          </h3>
          <MatchSettingsComponent
            key={formData.sportTypeId}
            compact
            sportType={selectedSportName}
            initialSettings={formData.matchConfiguration}
            disabled={disabled}
            showNextButton={false}
            participantsTypeValue={formData.participantsType}
            onSettingsChange={(newSettings) =>
              setFormData(prev => ({ ...prev, matchConfiguration: newSettings }))
            }
            onParticipantsTypeChange={(value) =>
              setFormData(prev => ({ ...prev, participantsType: value }))
            }
          />
        </div>
      )}

      {pickerInput}

      {/* Submit Buttons - only show if not disabled */}
      {!disabled && (
        <div className="flex gap-x-6 justify-end">
          {onCancel && (
            <button
              type="button"
              onClick={handleCancel}
              className={`px-6 py-2 border ${theme.border} ${theme.text} rounded-lg ${theme.cardHover} transition-colors`}
            >
              Cancel
            </button>
          )}
          <button
            type="submit"
            disabled={loading}
            className={`${theme.gradient} text-white px-8 py-2 rounded-lg hover:shadow-lg transition-shadow disabled:opacity-50`}
          >
            {loading ? 'Saving...' : submitLabel}
          </button>
        </div>
      )}
    </form>

    <ConfirmModal
      isOpen={showCancelConfirm}
      onClose={() => setShowCancelConfirm(false)}
      onConfirm={() => {
        setShowCancelConfirm(false);
        onCancel();
      }}
      title="Discard Changes"
      message="You have unsaved changes. Are you sure you want to cancel? Your changes will be lost."
      confirmText="Discard"
      cancelText="Keep Editing"
      confirmStyle="danger"
    />
    </>
  );
});

TournamentForm.displayName = 'TournamentForm';

export default TournamentForm;